import { Grid, Stack } from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LabeledInput } from '@work4all/components/lib/input/labeled-input';
import { NumberInput } from '@work4all/components/lib/input/number-input';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { useDataMutation } from '@work4all/data';

import { Country } from '@work4all/models/lib/Classes/Country.entity';
import { TaxKey } from '@work4all/models/lib/Classes/TaxKey.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { BaseDataLanguagePickerField } from '../../../../../components/entity-picker/BaseDataLanguagePickerField';
import { CurrencyPickerField } from '../../../../../components/entity-picker/CurrencyPickerField';
import { TaxGroupPickerField } from '../../../../../components/entity-picker/TaxGroupPickerField';
import { TaxKeyPickerField } from '../../../../../components/entity-picker/TaxKeyPickerField';
import { ControllerPlus } from '../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../form-plus/use-form-context-plus';
import { CheckboxRadioItem } from '../../../locked-inputs';
import { Collapse, ControlWrapper } from '../../components';
import { useMaskConfig } from '../../hooks/mask-context';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';
import { pickUpdateFields } from '../../utils/pick-update-fields';
import { useFormUpdate } from '../../utils/use-form-update';

type CountryFormValue = PathsOf<Country, 2>;

type CountryOverlayControllerProps = MaskControllerProps & {
  amplitudeEntryPoint: string;
};

const DEFAULT_VALUES = {
  taxGroup: null,
  currency: null,
  language: null,
  noVat: true,
  taxKey1: 0,
  taxKey2: 0,
  taxKey3: 0,
  travelExpenseAbove24h: 0,
  travelExpenseAbove14h: 0,
  travelExpenseAbove8h: 0,
  travelExpenseBelow24h: 0,
  overnightAccommodationAllowance: 0,
  breakfast: 0,
  reductionFood: 0,
};

export function CountryOverlayController(props: CountryOverlayControllerProps) {
  const { amplitudeEntryPoint } = props;

  const { t } = useTranslation();

  const mask = useMaskConfig(props);

  const request = useCountryRequestData(mask.id);

  const customRules = useCallback(
    (data: CountryFormValue) => {
      if (!data.shortName) {
        return {
          shortName: {
            message: t('ERROR.FIELD_REQUIRED'),
            type: 'customValidation',
          },
        };
      }
      return true;
    },
    [t]
  );

  const { ...mutationData } = request.data as Country;
  const [mutate] = useDataMutation<Country, EMode.upsert>({
    entity: mask.entity,
    mutationType: EMode.upsert,
    responseData: mutationData,
    onCompleted: (data) => {
      props.onAfterSave?.(data);
    },
  });

  useEffect(() => {
    if (mask.isCreateMode) {
      sendAmplitudeData(EventType.AddCountry, {
        entryPoint: amplitudeEntryPoint,
      });
    } else {
      sendAmplitudeData(EventType.EditCountry, {
        entryPoint: amplitudeEntryPoint,
      });
    }
  }, [amplitudeEntryPoint, mask, mask.isCreateMode, props.id]);

  const overlay = useMaskOverlay<CountryFormValue>({
    ...props,
    request,
    newEntityData: DEFAULT_VALUES,
    mask,
    getSubTitle: (x) => x.longtext,
    customRules,
  });

  const { formState } = overlay.form;

  const handleSubmit = useEventCallback(
    async (formValues: CountryFormValue) => {
      const update = mask.isCreateMode
        ? formValues
        : pickUpdateFields(formValues, formState.dirtyFields);

      await mutate(update);

      sendAmplitudeData(EventType.SaveCountry, {
        entryPoint: amplitudeEntryPoint,
      });
    }
  );

  useFormUpdate(
    {
      taxGroup: (taxGroup) => {
        return { taxGroupId: taxGroup?.id ?? null };
      },
      language: (language) => {
        return { languageId: language?.id ?? null };
      },
      currency: (currency) => {
        return { currencyId: currency?.id ?? null };
      },
    },
    overlay.form
  );

  return (
    <OverlayController<CountryFormValue> {...overlay} onSubmit={handleSubmit}>
      <CountryMaskContent />
    </OverlayController>
  );
}

function CountryMaskContent() {
  const { t } = useTranslation();

  const { register, setValue, control } =
    useFormContextPlus<CountryFormValue>();

  return (
    <>
      <Collapse title={t('COMMON.GENERAL')} defaultOpen>
        <Stack gap="1rem">
          <Grid container spacing="1rem">
            <Grid item xs={12} sm={6}>
              <LabeledInput
                label={t('COMMON.COUNTRY_NAME')}
                {...register('longtext')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <ControlWrapper>
                <LabeledInput
                  label={t('COMMON.COUNTRY_SYMBOL')}
                  {...register('shortName')}
                  required
                />
              </ControlWrapper>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabeledInput
                label={t('COMMON.COUNTRY_SYMBOL2')}
                {...register('shortName2')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabeledInput
                label={t('COMMON.COUNTRY_SYMBOL3')}
                {...register('shortName3')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <ControllerPlus
                name="language"
                control={control}
                render={({ field: languageField }) => {
                  return <BaseDataLanguagePickerField {...languageField} />;
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LabeledInput
                label={t('COMMON.COUNTRY_AREA_CODE')}
                {...register('areacode')}
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <ControllerPlus
                control={control}
                name="anglosaxon"
                render={({ field: { value: checked, ...field } }) => {
                  return (
                    <CheckboxRadioItem
                      control="checkbox"
                      label={t('COMMON.COUNTRY_ANGLOSAXON')}
                      checked={checked}
                      defaultChecked={checked}
                      {...field}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
        </Stack>
      </Collapse>
      <Collapse title={t('MASK.PRICE_AND_TAX')} defaultOpen>
        <Grid container spacing="1rem">
          <Grid item xs={12} sm={6} md={3}>
            <ControllerPlus
              name="taxGroup"
              control={control}
              render={({ field }) => {
                return <TaxGroupPickerField {...field} clearable />;
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <ControllerPlus
              name="currency"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <CurrencyPickerField
                    {...field}
                    error={fieldState?.error?.message}
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <ControllerPlus
              control={control}
              name="noVat"
              render={({ field: { value: checked, ...field } }) => {
                return (
                  <CheckboxRadioItem
                    control="checkbox"
                    label={t('INPUTS.NO_VAT')}
                    checked={checked}
                    defaultChecked={checked}
                    {...field}
                  />
                );
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing="1rem" padding="1rem 0">
          <Grid item xs={12} sm={6} md={3}>
            <ControllerPlus
              name="taxKey1"
              control={control}
              render={({ field }) => (
                <TaxKeyPickerField
                  value={{
                    id: field.value ?? 0,
                    taxKeyValue: field.value ?? 0,
                  }}
                  onChange={(value: TaxKey) =>
                    setValue('taxKey1', value.taxKeyValue ?? 0, {
                      shouldDirty: true,
                    })
                  }
                  view="field"
                  label={t('COMMON.COUNTRY_TAX_KEY_1')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ControllerPlus
              name="taxKey2"
              control={control}
              render={({ field }) => (
                <TaxKeyPickerField
                  value={{
                    id: field.value ?? 0,
                    taxKeyValue: field.value ?? 0,
                  }}
                  onChange={(value: TaxKey) =>
                    setValue('taxKey2', value.taxKeyValue ?? 0, {
                      shouldDirty: true,
                    })
                  }
                  view="field"
                  label={t('COMMON.COUNTRY_TAX_KEY_2')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ControllerPlus
              name="taxKey3"
              control={control}
              render={({ field }) => (
                <TaxKeyPickerField
                  value={{
                    id: field.value ?? 0,
                    taxKeyValue: field.value ?? 0,
                  }}
                  onChange={(value: TaxKey) =>
                    setValue('taxKey3', value.taxKeyValue ?? 0, {
                      shouldDirty: true,
                    })
                  }
                  view="field"
                  label={t('COMMON.COUNTRY_TAX_KEY_3')}
                />
              )}
            />
          </Grid>
        </Grid>
      </Collapse>
      <Collapse title={t('COMMON.EXPENSES')} defaultOpen>
        <Grid container spacing="1rem">
          <Grid item xs={12} sm={6} md={3}>
            <ControllerPlus
              name="travelExpenseAbove24h"
              control={control}
              render={({ field }) => (
                <NumberInput
                  {...field}
                  selectOnFocus
                  label={t('COMMON.COUNTRY_TRAVEL_EXPENSE_ABOVE_24H')}
                  min={0}
                  decimals={2}
                  hideControls
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ControllerPlus
              name="travelExpenseBelow24h"
              control={control}
              render={({ field }) => (
                <NumberInput
                  {...field}
                  selectOnFocus
                  label={t('COMMON.COUNTRY_TRAVEL_EXPENSE_BELOW_24H')}
                  min={0}
                  decimals={2}
                  hideControls
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing="1rem" padding="1rem 0">
          <Grid item xs={12} sm={6} md={3}>
            <ControllerPlus
              name="overnightAccommodationAllowance"
              control={control}
              render={({ field }) => (
                <NumberInput
                  {...field}
                  selectOnFocus
                  label={t('COMMON.COUNTRY_OVERNIGHT_ALLOWANCE')}
                  min={0}
                  decimals={2}
                  hideControls
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <ControllerPlus
              name="breakfast"
              control={control}
              render={({ field }) => (
                <NumberInput
                  {...field}
                  selectOnFocus
                  label={t('COMMON.COUNTRY_BREAKFAST')}
                  min={0}
                  decimals={2}
                  hideControls
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <ControllerPlus
              name="reductionFood"
              control={control}
              render={({ field }) => (
                <NumberInput
                  {...field}
                  selectOnFocus
                  label={t('COMMON.COUNTRY_REDUCTION_FOOD')}
                  min={0}
                  decimals={2}
                  hideControls
                />
              )}
            />
          </Grid>
        </Grid>
      </Collapse>
    </>
  );
}

export const useCountryRequestData = (id: string | number) => {
  return useMemo<DataRequest>(() => {
    const countryRequestData: Country<EMode.query> = {
      id: null,
      shortName: null,
      shortName2: null,
      shortName3: null,
      longtext: null,
      taxKey1: null,
      taxKey2: null,
      taxKey3: null,
      anglosaxon: null,
      areacode: null,
      travelExpenseAbove24h: null,
      travelExpenseBelow24h: null,
      overnightAccommodationAllowance: null,
      breakfast: null,
      reductionFood: null,
      noVat: null,
      language: {
        id: null,
        name: null,
      },
      currency: {
        id: null,
        name: null,
      },
      taxGroup: {
        id: null,
        name: null,
        type: null,
      },
    };
    const filter = [{ id: { $eq: id } }];
    const data = countryRequestData;
    return {
      entity: Entities.country,
      data,
      filter,
    };
  }, [id]);
};
